import flatten from 'flat';
import { Fragment, useRef, useEffect } from 'react';
import { useFormikContext } from 'formik';
import { jsx } from '@emotion/react/jsx-runtime';

/**
 * Use FocusOnError when Formik is in the same component and you don't have
 * access to useFormikContext, otherwise useFocusOnError can be used.
 */
function FocusOnError(_ref) {
  let {
    initialValidation = false,
    focusDelay = 0
  } = _ref;
  useFocusOnError(initialValidation, focusDelay);
  return jsx(Fragment, {});
}

/**
 * Focus the first element with error in the form after triggering Formik onSubmit.
 * If used with initialValidation `true`, it will focus after form initialization.
 * * Important * The order is based on the errors object, and the errors key order is based on
 * the order used in the validation schema.
 *
 * @param initialValidation boolean true if the focus is desired on form initialization
 * @param focusDelay number milliseconds to delay the focus on the input
 */
function useFocusOnError() {
  let initialValidation = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
  let focusDelay = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  const {
    errors,
    isSubmitting,
    isValidating,
    validateForm
  } = useFormikContext();
  const validateOnInit = useRef(initialValidation);
  useEffect(() => {
    if (validateOnInit.current) {
      runImperativeValidation();
      validateOnInit.current = false;
      return;
    }
    if (isSubmitting && !isValidating) {
      focusFirstElementWithError(errors);
    }
    function focusFirstElementWithError(errorsObject) {
      if (!Object.keys(errorsObject).length) return;
      const flattenedErrorsObj = flatten(errorsObject);
      const names = Object.keys(flattenedErrorsObj);
      const firstErrorElementName = names === null || names === void 0 ? void 0 : names[0];
      if (firstErrorElementName) {
        const element = window.document.querySelector(`form [name="${firstErrorElementName}"]`);
        element === null || element === void 0 ? void 0 : element.scrollIntoView({
          behavior: 'smooth'
        });
        setTimeout(() => {
          element === null || element === void 0 ? void 0 : element.focus();
        }, focusDelay);
      }
    }
    async function runImperativeValidation() {
      const validationErrors = await validateForm();
      focusFirstElementWithError(validationErrors);
    }
  }, [errors, focusDelay, isSubmitting, isValidating, validateForm]);
}

export { FocusOnError, useFocusOnError };
//# sourceMappingURL=FocusOnError.js.map
